
import { swapLayout } from './layout';

export type Range = [start: number, end: number];

export interface MatchResult {
  score: number;
  ranges: Range[];
}

export function normalize(text: string): string {
  return text.toLowerCase().replace(/ё/g, 'е');
}

const isWordStart = (text: string, index: number) => index === 0 || !/[\p{L}\p{N}]/u.test(text[index - 1]);

function mergeRanges(ranges: Range[]): Range[] {
  const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
  const out: Range[] = [];
  for (const r of sorted) {
    const last = out[out.length - 1];
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
    else out.push([r[0], r[1]]);
  }
  return out;
}

function matchWords(hay: string, words: string[]): { ranges: Range[]; wordStarts: number } | null {
  const ranges: Range[] = [];
  let wordStarts = 0;
  for (const word of words) {
    let at = -1;
    for (let i = hay.indexOf(word); i !== -1; i = hay.indexOf(word, i + 1)) {
      if (isWordStart(hay, i)) {
        at = i;
        break;
      }
      if (at === -1) at = i;
    }
    if (at === -1) return null;
    if (isWordStart(hay, at)) wordStarts += 1;
    ranges.push([at, at + word.length]);
  }
  return { ranges: mergeRanges(ranges), wordStarts };
}

function matchSubsequence(hay: string, needle: string): Range[] | null {
  const ranges: Range[] = [];
  let from = 0;
  for (const ch of needle) {
    if (ch === ' ') continue;
    const i = hay.indexOf(ch, from);
    if (i === -1) return null;
    ranges.push([i, i + 1]);
    from = i + 1;
  }
  return mergeRanges(ranges);
}

export function matchText(text: string, query: string, { fuzzy = true } = {}): MatchResult | null {
  const hay = normalize(text);
  const needle = normalize(query.trim());
  if (!needle) return { score: 0, ranges: [] };

  if (hay === needle) return { score: 100, ranges: [[0, hay.length]] };
  if (hay.startsWith(needle)) return { score: 85, ranges: [[0, needle.length]] };

  const words = needle.split(/\s+/).filter(Boolean);
  const byWords = matchWords(hay, words);
  if (byWords) {
    const share = byWords.wordStarts / words.length;
    return { score: 45 + Math.round(share * 25), ranges: byWords.ranges };
  }

  if (!fuzzy || needle.length < 2) return null;
  const sub = matchSubsequence(hay, needle);
  if (!sub) return null;
  const pieces = sub.length;
  const letters = needle.replace(/\s/g, '').length;
  if (pieces > Math.max(2, Math.ceil(letters / 2))) return null;
  return { score: 30 - pieces * 3, ranges: sub };
}

export function matchCommand(label: string, keywords: readonly string[], query: string): MatchResult | null {
  const variants: { q: string; weight: number }[] = [{ q: query, weight: 1 }];
  const swapped = swapLayout(query);
  if (swapped) variants.push({ q: swapped, weight: 0.9 });

  let best: MatchResult | null = null;
  for (const { q, weight } of variants) {
    const direct = matchText(label, q);
    if (direct && (!best || direct.score * weight > best.score)) {
      best = { score: direct.score * weight, ranges: direct.ranges };
    }
    for (const kw of keywords) {
      const viaKeyword = matchText(kw, q, { fuzzy: false });
      if (viaKeyword && (!best || viaKeyword.score * weight * 0.8 > best.score)) {
        best = { score: viaKeyword.score * weight * 0.8, ranges: [] };
      }
    }
  }
  return best;
}
